import { useMutation, useQueryClient } from '@tanstack/react-query';

import { getAccessToken } from '@/lib/tokenStorage';

import type { AnimalPayload } from './api';
import type { AnimalRow, AnimalTypeLabel } from './index';

export type EditAnimalInput = {
  id: string;
  name?: string;
  tag?: string;
  registration?: string;
  metalTag?: string;
  breedId?: string;
  ownerId?: string;
  yardId?: string;
  penId?: string;
  birth?: string;
  weight?: number;
  statusCode?: number;
  programCategory?: string;
  opuProgram?: string;
  pregnant?: number;
  received?: string;
  shipOn?: string;
  origin?: string;
  hairSample?: boolean;
  heiferCow?: number;
  lastDrugUsedId?: string;
  client?: number;
  colorId?: string;
  ocvStatusId?: string;
  recipientStatusId?: string;
  sex?: number;
};

export type CreateAnimalProcedureInput = {
  procedureCodeId: string;
  procedureDate: string;
  description?: string;
  performedById?: string;
};

/** Values the edit dialogs work with, keyed like AnimalRow. */
export type AnimalEditValues = {
  name: string;
  tag: string;
  registration: string;
  rfidTag: string;
  breedId: string | null;
  ownerId: string | null;
  yardId: string | null;
  penId: string | null;
  birthDate: string;
  weight: number | null;
  status: 'active' | 'inactive';
  programCategory: string;
  opuProgram: string;
  pregnant: AnimalRow['pregnant'];
  receivedDate: string;
  shipDate: string;
  origin: string;
  hairSample: AnimalRow['hairSample'];
  heiferCow: string;
  lastDrugUsedId: string | null;
  client: string;
  colorId: string | null;
  ocvStatusId: string | null;
  recipientStatusId: string | null;
  sex: string;
};

export type WritableField = keyof AnimalEditValues;

// Fields Nucleus shows and edits per type: the #35316 Required and Visible
// columns that have a legacy table column (see the Writes section of
// TogApi/Repositories/field-mapping.md). "Not Required" fields (donor Our Tag
// and Target Weight, recip Registry) are hidden and never sent, so they stay
// as they are in the database. LOPU Donor has no legacy table yet.
const COMMON_FIELDS: WritableField[] = [
  'rfidTag',
  'breedId',
  'ownerId',
  'yardId',
  'shipDate',
  'status',
  'origin',
];

export const WRITABLE_FIELDS: Record<
  AnimalTypeLabel,
  ReadonlySet<WritableField>
> = {
  Donor: new Set<WritableField>([
    ...COMMON_FIELDS,
    'name',
    'registration',
    'birthDate',
    'pregnant',
    'programCategory',
    'opuProgram',
    'receivedDate',
    'hairSample',
    'heiferCow',
    'ocvStatusId',
  ]),
  Recipient: new Set<WritableField>([
    ...COMMON_FIELDS,
    'tag',
    'weight',
    'receivedDate',
    'lastDrugUsedId',
    'client',
    'colorId',
    'ocvStatusId',
    'recipientStatusId',
  ]),
  Calf: new Set<WritableField>([
    ...COMMON_FIELDS,
    'tag',
    'registration',
    'weight',
    'name',
    'birthDate',
    'penId',
    'opuProgram',
    'colorId',
    'sex',
  ]),
  'LOPU Donor': new Set<WritableField>(),
};

export const isWritable = (type: AnimalTypeLabel, field: WritableField) =>
  WRITABLE_FIELDS[type].has(field);

/** Date-only string (yyyy-MM-dd) from an API timestamp or a date-only value. */
export const toDateOnly = (value: string | null | undefined): string =>
  value ? value.slice(0, 10) : '';

export const toEditValues = (row: AnimalRow): AnimalEditValues => ({
  name: row.name,
  tag: row.tag,
  registration: row.registration,
  rfidTag: row.rfidTag,
  breedId: row.breedId,
  ownerId: row.ownerId,
  yardId: row.yardId,
  penId: row.penId,
  birthDate: toDateOnly(row.birthDate),
  weight: row.weight,
  status: row.stateCode === 1 ? 'inactive' : 'active',
  programCategory: row.programCategory,
  opuProgram: row.opuProgram,
  pregnant: row.pregnant,
  receivedDate: toDateOnly(row.receivedDate),
  shipDate: toDateOnly(row.shipDate),
  origin: row.origin,
  hairSample: row.hairSample,
  heiferCow: row.heiferCow,
  lastDrugUsedId: row.lastDrugUsedId,
  client: row.client,
  colorId: row.colorId,
  ocvStatusId: row.ocvStatusId,
  recipientStatusId: row.recipientStatusId,
  sex: row.sex,
});

type FieldWriter = (input: EditAnimalInput, value: never) => void;

const WRITERS: Record<WritableField, FieldWriter> = {
  name: (i, v: string) => (i.name = v),
  tag: (i, v: string) => (i.tag = v),
  registration: (i, v: string) => (i.registration = v),
  rfidTag: (i, v: string) => (i.metalTag = v),
  breedId: (i, v: string) => (i.breedId = v),
  ownerId: (i, v: string) => (i.ownerId = v),
  yardId: (i, v: string) => (i.yardId = v),
  penId: (i, v: string) => (i.penId = v),
  birthDate: (i, v: string) => (i.birth = v),
  weight: (i, v: number) => (i.weight = v),
  status: (i, v: AnimalEditValues['status']) =>
    (i.statusCode = v === 'inactive' ? 2 : 1),
  programCategory: (i, v: string) => (i.programCategory = v),
  opuProgram: (i, v: string) => (i.opuProgram = v),
  pregnant: (i, v: 'Yes' | 'No') => (i.pregnant = v === 'Yes' ? 1 : 0),
  receivedDate: (i, v: string) => (i.received = v),
  shipDate: (i, v: string) => (i.shipOn = v),
  origin: (i, v: string) => (i.origin = v.toUpperCase()),
  hairSample: (i, v: 'Yes' | 'No') => (i.hairSample = v === 'Yes'),
  heiferCow: (i, v: string) => (i.heiferCow = Number(v)),
  lastDrugUsedId: (i, v: string) => (i.lastDrugUsedId = v),
  client: (i, v: string) => (i.client = Number(v)),
  colorId: (i, v: string) => (i.colorId = v),
  ocvStatusId: (i, v: string) => (i.ocvStatusId = v),
  recipientStatusId: (i, v: string) => (i.recipientStatusId = v),
  sex: (i, v: string) => (i.sex = Number(v)),
};

const isEmpty = (value: unknown) =>
  value === null || value === undefined || value === '';

export type EditAnimalDiff = {
  /** Null when nothing writable changed. */
  input: EditAnimalInput | null;
  /**
   * Fields the user emptied. The API treats null as "leave unchanged", so a
   * clear cannot be expressed yet; the caller should flag these.
   */
  cleared: WritableField[];
};

/**
 * Builds the PUT body for one animal: only fields that changed and that the
 * animal's legacy table can store.
 */
export const buildEditAnimalInput = (
  row: AnimalRow,
  changes: Partial<AnimalEditValues>,
): EditAnimalDiff => {
  const original = toEditValues(row);
  const input: EditAnimalInput = { id: row.id };
  const cleared: WritableField[] = [];
  let changed = false;

  for (const [key, raw] of Object.entries(changes)) {
    const field = key as WritableField;
    if (!isWritable(row.animalType, field)) continue;
    const value = typeof raw === 'string' ? raw.trim() : raw;
    if (value === original[field]) continue;
    if (isEmpty(value)) {
      if (!isEmpty(original[field])) cleared.push(field);
      continue;
    }
    WRITERS[field](input, value as never);
    changed = true;
  }

  return { input: changed ? input : null, cleared };
};

// API error keys (EditAnimalInput property names) back to form field names.
export const EDIT_ERROR_FIELDS: Record<string, WritableField> = {
  name: 'name',
  tag: 'tag',
  registration: 'registration',
  metalTag: 'rfidTag',
  breedId: 'breedId',
  ownerId: 'ownerId',
  yardId: 'yardId',
  penId: 'penId',
  birth: 'birthDate',
  weight: 'weight',
  statusCode: 'status',
  programCategory: 'programCategory',
  opuProgram: 'opuProgram',
  pregnant: 'pregnant',
  received: 'receivedDate',
  shipOn: 'shipDate',
  origin: 'origin',
  hairSample: 'hairSample',
  heiferCow: 'heiferCow',
  lastDrugUsedId: 'lastDrugUsedId',
  client: 'client',
  colorId: 'colorId',
  ocvStatusId: 'ocvStatusId',
  recipientStatusId: 'recipientStatusId',
  sex: 'sex',
};

async function send<T>(
  method: 'POST' | 'PUT',
  path: string,
  body: unknown,
): Promise<T> {
  const token = getAccessToken();
  if (!token) {
    throw new Error('No access token available. Please log in.');
  }
  const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}${path}`, {
    method,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });
  const text = await response.text();
  if (!text) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return JSON.parse(text) as T;
}

const failed = (message: string): AnimalPayload => ({
  code: 0,
  success: false,
  message,
  data: null,
  errors: null,
});

/**
 * PUT /api/v1/animal. Returns one payload per input, in order. A request-level
 * failure (single payload or network error) is applied to every input.
 */
export async function editAnimals(
  inputs: EditAnimalInput[],
): Promise<AnimalPayload[]> {
  try {
    const result = await send<AnimalPayload[] | AnimalPayload>(
      'PUT',
      '/api/v1/animal',
      inputs,
    );
    if (Array.isArray(result)) return result;
    return inputs.map(() => result);
  } catch (error) {
    const payload = failed((error as Error).message);
    return inputs.map(() => payload);
  }
}

export async function createAnimalProcedure(
  animalId: string,
  input: CreateAnimalProcedureInput,
): Promise<AnimalPayload> {
  try {
    return await send<AnimalPayload>(
      'POST',
      `/api/v1/animal/${animalId}/procedure`,
      input,
    );
  } catch (error) {
    return failed((error as Error).message);
  }
}

export type ProcedureRequest = {
  animalId: string;
  input: CreateAnimalProcedureInput;
};

export async function createAnimalProcedures(
  requests: ProcedureRequest[],
): Promise<AnimalPayload[]> {
  return Promise.all(
    requests.map((r) => createAnimalProcedure(r.animalId, r.input)),
  );
}

const useInvalidateAnimalManagement = () => {
  const queryClient = useQueryClient();
  return () =>
    queryClient.invalidateQueries({ queryKey: ['animal-management'] });
};

export const useEditAnimals = () => {
  const invalidate = useInvalidateAnimalManagement();
  return useMutation({
    mutationFn: editAnimals,
    onSettled: invalidate,
  });
};

export const useCreateAnimalProcedures = () => {
  const invalidate = useInvalidateAnimalManagement();
  return useMutation({
    mutationFn: createAnimalProcedures,
    onSettled: invalidate,
  });
};

/** First server message across failed payloads, for a summary toast. */
export const firstFailureMessage = (payloads: AnimalPayload[]) =>
  payloads.find((p) => !p.success)?.message ??
  Object.values(payloads.find((p) => !p.success)?.errors ?? {})[0] ??
  null;
